/**
 * O mixin que resolve partes de tool em CARTÕES — a lógica que as três
 * superfícies do chat compartilham ([I-1] do review do PR #2086).
 *
 * ★ Por que um mixin, e não só o componente `partes-do-turno.vue`. A bolha do
 *   chat (`chat-message.vue`) monta os cartões DENTRO dela, entre o texto e as
 *   fontes, e as suítes que a medem usam `shallowMount` — com o renderer como
 *   componente filho, todo teste de cartão de lá passaria a medir um stub vazio
 *   (`stubs: {PartesDoTurno: false}` não é honrado no @vue/test-utils 1.3.0
 *   desta casa; medido em 26/08/2026). Então a bolha usa este mixin e mantém o
 *   laço no próprio template, e as duas superfícies do Fee Fixo usam o
 *   componente — que também é este mixin. A REGRA é uma só; o que se repete é
 *   o laço de 12 linhas, e há um teste que compara os dois textualmente.
 *
 * ## O contrato do mixin — TRÊS coisas, não uma
 *
 * | o quê | obrigatório? | sem ele |
 * |---|---|---|
 * | computed `partesParaRenderizar` | **sim** | `partesResolvidas` fica vazia |
 * | `storeModule` (data/prop/computed) | na prática, sim | `partePorId` lê `undefined/toolPartPorId`, não acha getter, e devolve `null`: a parte VIVA para de vencer a cópia persistida, e o botão fica de pé sobre uma ação já executada |
 * | `$access` (mixin global do app) | sim | `resolverEntrada` é fail-closed na `feature` e TODO cartão de módulo vira `fallback-card` com `flag_desligada` |
 * | computed `turnoEmVoo` | na prática, sim | a moldura manda `undefined` ao cartão, e o cartão que autoriza escrita deixa de desabilitar os botões enquanto o turno corre ([P1] da corrida do T023) |
 * | prop/data `chavesIgnoradas` | opcional | nenhuma chave é descartada (o default) |
 *
 * ## O cartão entra quando o resultado chega — sem indicador genérico (11/09/2026)
 *
 * Entre 30/08 e 11/09 o laço escondia TODOS os cartões resolvidos atrás de um
 * `CartaoPendente` ("Montando a resposta...") enquanto o turno corria, para o
 * cartão não "pular pronto" antes do texto acabar. O custo era um segundo
 * indicador de trabalho na mesma bolha, empilhado sobre o rótulo de fase do
 * rodapé — e o usuário pediu UM só. Hoje:
 *
 * - a parte que o fio ABRIU e ainda não respondeu (`input-available` de tool de
 *   servidor) não desenha nada (`esqueletoDoFio`, `partes.js`): quem diz
 *   "Consultando cliente..." é o rótulo único do turno;
 * - o cartão entra no instante do `tool-output-available`, com a animação de
 *   entrada do laço (`transition-group`, classes `iv2-parte-*` de
 *   `design/efeitos.sass`);
 * - o esqueleto ficou só para a janela de validação do schema — `validando:
 *   true` na parte resolvida vira a classe `iv2-parte--validando`, e a troca
 *   esqueleto → conteúdo é um crossfade curto SEM remontar (mesma `identidade`).
 *
 * Os do meio não estavam escritos aqui, e essas ausências são SILENCIOSAS —
 * nada estoura, o cartão só passa a desenhar outra coisa. Quem monta o mixin
 * fora de um componente do app precisa prover os que se aplicam.
 *
 * ## O painel de artefato (Fase 2b, plano `moonlit-conjuring-sun`)
 *
 * `montarCartao` decide POR CARTÃO se o cartão de dados vai para a bolha
 * (inline) ou para o painel (prévia + aside). Desde 14/09/2026 são duas
 * coisas diferentes:
 *
 * - o getter `<storeModule>/modoArtefatoAtivo` (`store/artefato.js`) é
 *   condição NECESSÁRIA — diz que há painel disponível nesta superfície
 *   (kill-switch ligado E imersiva). Falso ⇒ tudo inline, como sempre foi;
 * - quem decide por cartão é o BACKEND, via `ui.apresentacao: 'inline' |
 *   'painel'` na parte da tool. Ausente ⇒ painel. A régua (com as duas regras
 *   estruturais do front: painel indisponível e cartão de ESCRITA são sempre
 *   inline) é `ui-registry/apresentacao.js::resolverApresentacao`.
 *
 * Quando a resposta é `painel` e a entrada resolvida é um CARTÃO DE DADOS
 * (`ehCartaoDeDados` — tem `component`+`schema`, não é uma tool de front
 * `executor: 'client'`), o cartão que a tela recebe é `ArtefatoPreviewCard`
 * (ícone + título), e não o componente de verdade. A resolução REAL fica
 * anexada em `parteReal` (`{identidade, componente, props, label, module}`)
 * — é o que `chat-message.vue` usa para despachar `abrirArtefato` no clique.
 *
 * ★ [redesign visual] `parteReal.identidade` é OBRIGATÓRIA a partir daqui:
 * `chat-message.vue::onAbrirArtefato` despacha `parte.parteReal` (não `parte`
 * inteira) para a action `abrirArtefato` do `store/artefato.js`, que grava o
 * payload em `state.parte` — e é de lá que `artefatoIdentidadeAtiva`/
 * `MARCAR_ARTEFATO_VISTO` leem `.identidade` para decidir os estados
 * "ativo"/"visto" do PRÓXIMO card renderizado com a mesma identidade. Sem
 * este campo em `parteReal`, o aside abre normalmente (`componente`/`props`
 * bastam pra isso), mas nenhum card jamais aparenta estar "ativo" nem "visto"
 * — os dois estados ficariam permanentemente inalcançáveis.
 *
 * ★ Nos DOIS modos o cartão de dados sai com `artefato` (título/ícone/
 * subtítulo/módulo — `comMetadadosDeArtefato`), `ancoraDeArtefato` (a
 * `identidade`, que a bolha carimba como `data-artefato-id` no invólucro) e
 * o MESMO `parteReal` (`parteRealDe`): é o que o índice "Artefatos desta
 * conversa" registra, e o clique na linha abre o aside TAMBÉM para o cartão
 * inline — o aside é só outra forma de ver o mesmo cartão (decisão do
 * usuário, 14/09/2026: "tudo no aside"). `parteReal` fica `null` em dois
 * casos, e só neles: cartão de ESCRITA (ver `abreNoAside`) e painel
 * indisponível (`modoArtefatoAtivo` falso — não há aside); aí o índice rola
 * até o `data-artefato-id` em vez de abrir.
 *
 * ★ O getter só existe (e só é `true`) no namespace `aawz-intelligence-v2`,
 * ligado pela visão imersiva (`/home-ia`). Nas outras superfícies que também
 * usam este mixin (drawer do Especialista e Guia do Fee Fixo, `feeFixoIa`) o
 * getter não existe — `this.$store.getters[...]` devolve `undefined`, que vira
 * `false` — então elas continuam montando o cartão real como sempre montaram.
 *
 * ## Abertura automática de documento (spec-013 [T007], US-5)
 *
 * Um único cartão do registry (`documento/panorama`) pede pra o painel abrir
 * SOZINHO, sem esperar o clique na prévia — é o resultado do `/panorama`
 * (US-5: "o painel abre com o documento"). A entrada do registry declara
 * `abrirNoPainelAutomaticamente: true`; `montarCartao` só deixa a flag chegar
 * a `true` em `parte.abrirAutomaticamente` depois do MESMO veredito de schema
 * que qualquer cartão de dados exige, e nunca para uma parte PERSISTIDA
 * (`abreNoPainelAutomaticamente`, perto de `montarPreviewDeArtefato`) — é
 * assim que "schema inválido não abre" e "troca de conversa não abre
 * documento antigo" (`plan.md` §5) ficam garantidos SEM o watch abaixo saber
 * de nada disso. O `watch: partesResolvidas` só varre o que já saiu
 * resolvido e despacha `abrirArtefato` uma vez por `identidade`
 * (`documentosAbertosAutomaticamente`, em `data()`) — o mesmo dispatch que o
 * clique na prévia faria, só que sem esperar o clique.
 */
import {
  MOTIVOS,
  resolverEntrada,
  validarProps,
} from '@modules/aawz-intelligence-v2/ui-registry'
import FallbackCard from '@modules/aawz-intelligence-v2/ui-registry/fallback-card.vue'
import ArtefatoPreviewCard from '@modules/aawz-intelligence-v2/ui-registry/artefato-preview-card.vue'
import { resolverIconeDoModulo } from '@modules/aawz-intelligence-v2/ui-registry/utils/resolver-icone-do-modulo'
import { resolverNomeDoModulo } from '@modules/aawz-intelligence-v2/ui-registry/utils/resolver-nome-do-modulo'
import { tituloDoArtefato } from '@modules/aawz-intelligence-v2/ui-registry/utils/titulo-do-artefato'
import { formatNumber } from '@utils/Format'
import {
  APRESENTACAO,
  ehCartaoDeEscrita,
  resolverApresentacao,
} from '@modules/aawz-intelligence-v2/ui-registry/apresentacao'
import {
  chaveDaToolDeFront,
  estadoDaAcaoPeloOutput,
  motivoDaAcaoPeloOutput,
  bloqueadoDaAcaoPeloOutput,
  hrefDaAcaoPeloOutput,
  uiDaAcaoDeFront,
  EXECUTOR_CLIENTE,
} from '@modules/aawz-intelligence-v2/ui-registry/chave-de-tool-de-front'
import { encenacaoDrenando } from '@modules/aawz-intelligence-v2/utils/encenacao-do-plano'
import {
  ESTADO_ESQUELETO,
  ESTADO_ERRO,
  ESTADO_RESOLVIDO,
  TITULO_TOOL_FALHOU,
  TITULO_TOOL_RECUSOU,
  chaveDaValidacao,
  ehCartaoDeDados,
  esqueletoDoFio,
  falhaSemCartao,
  partePersistida,
  parteDoResultadoBruto,
  payloadDeOffload,
  propsDaParte,
  recusaDaTool,
  escritaConcluidaSemProps,
  propostaPersistidaSemDesfecho,
  semPayloadParaDesenhar,
  textoDaFalha,
} from './partes'

// Tipo de visualização do card de prévia (redesign visual — mockups do
// usuário). Hoje quase todo cartão de dados registrado no `UI_REGISTRY` é
// desenhado por baixo por `cartao-tabela.vue`/`CartaoTabela`, então "Tabela" é
// um default razoável. Não existe metadata de "tipo de visualização" no
// registry ainda — se um cartão não-tabela (gráfico, lista) for registrado no
// futuro, isso precisa de um campo novo no registry; fora do escopo aqui.
const TIPO_ARTEFATO_PADRAO = 'Tabela'

export const renderizaPartesDoTurno = {
  data() {
    return {
      // `{ [chaveDaValidacao]: null | true | false }` — `null` é "validando".
      // Substituído inteiro a cada escrita: Vue 2 não observa chave nova.
      validacoes: {},
      // `{ [identidade]: true }` — as prévias que JÁ dispararam a abertura
      // automática do painel (spec-013 [T007]/US-5). Guarda de instância, não
      // de sessão: cada montagem do renderer (troca/reabertura de conversa)
      // começa vazia, e é a combinação com `!partePersistida(parte)`
      // (`abreNoPainelAutomaticamente`) que fecha os dois ângulos do risco do
      // `plan.md` ("replay, histórico ou remontagem disparam o watcher") —
      // este mapa cobre o replay DENTRO da mesma montagem; a guarda de
      // `persistida` cobre histórico/remontagem.
      documentosAbertosAutomaticamente: {},
    }
  },
  computed: {
    // Cada parte já decidida: o componente a montar (ou o fallback), os props e
    // o estado em que ele deve desenhar.
    /**
     * ★ [T075] A lista com o PONTEIRO de offload já trocado pela parte que o
     * `chat/resultado-bruto` desenha.
     *
     * Existe como ponto ÚNICO porque as duas pontas têm de ver a MESMA lista:
     * `partesResolvidas` (o que desenha) e `agendarValidacoes` (o que valida o
     * schema). Trocar só no desenho deixava a parte sintetizada sem validação
     * — veredito `null` para sempre, esqueleto eterno na tela. Foi o teste que
     * mostrou; a leitura do código não.
     */
    partesNormalizadas() {
      return (this.partesParaRenderizar || []).map(function(parte) {
        return payloadDeOffload(parte) ? parteDoResultadoBruto(parte) : parte
      })
    },
    partesResolvidas() {
      const self = this
      return (this.partesNormalizadas || [])
        // ★ [G-1 do QA] Parte sem `ui`, sem `output` e sem `state` não tem o
        // que desenhar — e um cartão de ERRO ali é pior que nada: era o "dados
        // fora do contrato" com Cancelar órfão que a conversa reaberta mostrava
        // sobre uma escrita já resolvida. O filtro mora no mixin, então vale
        // nas TRÊS superfícies.
        .filter(function(parte) {
          return !semPayloadParaDesenhar(parte)
        })
        .map(function(parte) {
          return self.resolverParte(parte)
        })
        // ★ A tool que não desenha cartão nenhum sai CALADA — `resolverParte`
        // devolve `null` para ela, e é aqui que ela deixa o thread. A maioria
        // das tools não tem cartão, e um "não consegui montar" por tool call
        // enche a conversa de erro onde nunca houve cartão para montar.
        .filter(function(cartao) {
          return Boolean(cartao)
        })
    },
    /**
     * O painel está DISPONÍVEL nesta superfície (Fase 2b, plano
     * `moonlit-conjuring-sun`) — condição necessária, não suficiente: por
     * cartão, quem decide é o `ui.apresentacao` do backend (`montarCartao`).
     *
     * Lido pelo `storeModule` da SUPERFÍCIE, nunca por um namespace fixo — nas
     * duas superfícies do Fee Fixo o getter não existe (o slice só é composto
     * em `aawz-intelligence-v2/store/index.js`), e `undefined` vira `false`
     * aqui, o mesmo contrato de `partePorId` acima.
     */
    modoArtefatoAtivo() {
      const store = this.$store
      if (!store) return false
      return Boolean(store.getters[this.storeModule + '/modoArtefatoAtivo'])
    },
  },
  watch: {
    partesParaRenderizar: {
      immediate: true,
      handler() {
        this.agendarValidacoes()
      },
    },
    /**
     * A abertura automática do painel (spec-013 [T007]/US-5) — pelo RENDERER
     * de verdade, nunca por quem monta um cartão isolado. `partesResolvidas`
     * já passou pelo `resolverParte` inteiro (falha primeiro, depois schema,
     * depois apresentação): é só DEPOIS desse `end`+validação que uma prévia
     * pode carregar `abrirAutomaticamente: true` (`montarPreviewDeArtefato`).
     *
     * ★ Mutação-sonda (A-7 do adversarial): apagar este `watch` — ou o
     * `autoAbrirDocumentos` que ele chama — deixa a suíte
     * `abertura-do-documento.unit.js` vermelha, porque o teste dela monta
     * `PartesDoTurno` de verdade e afere o `dispatch` no `$store`, nunca um
     * clique OU uma montagem direta do `.vue` do cartão.
     */
    partesResolvidas: {
      immediate: true,
      handler(partes) {
        this.autoAbrirDocumentos(partes)
      },
    },
  },
  methods: {
    /**
     * A parte que uma chamada persistida desenha ([T024]).
     *
     * ★ A parte VIVA vence. Depois do `end`, o cartão passa a ser desenhado
     * pela mensagem — mas o slice `toolParts` ainda tem a parte do turno, e é
     * ela que muda quando o gestor clica em "Abrir". Sem esta precedência o
     * botão continuaria de pé sobre uma ação já executada até o próximo reload.
     * Quando o turno seguinte limpa o slice, a cópia da mensagem assume — e ela
     * é a mesma que a conversa reaberta usa.
     */
    parteDaChamada(tc, indice) {
      const viva = this.partePorId(tc.id)
      if (viva) return viva
      const parte = {
        toolCallId: tc.id || 'tool-' + indice,
        tool: tc.name || '',
        label: tc.label || '',
        // ★ [STRESS r2, G-6] Faltava ler de volta o que `_registroDaParte`
        // (`apiChat.js`) agora escreve — sem isto, `textoDaFalha` (`partes.js`)
        // caía no genérico "a ferramenta falhou sem dizer o motivo" a partir
        // do turno seguinte, mesmo com o motivo real já tendo aparecido ao
        // vivo na mesma parte segundos antes.
        erro: tc.erro || '',
        input: tc.input || {},
        executor: tc.executor || '',
        // ★ O `state` PERSISTIDO manda (o backend passou a gravá-lo no
        // `tool_calls[]`: `approval-requested` no preview, `output-available`
        // no commit). O fallback cobre a chamada antiga, gravada antes do
        // campo existir — fixar `output-available` nela seria dizer que toda
        // proposta antiga foi confirmada.
        state: tc.state || ESTADO_RESOLVIDO,
        value: tc.output,
        ui: tc.ui,
        // ★ A ORIGEM da parte ([G-1] it.5). O turno vivo e a mensagem carregam
        // a mesma proposta em momentos diferentes: no turno ela é a decisão a
        // tomar (dois botões); aqui é história, e história não se autoriza de
        // novo. Sem esta marca, o único jeito de separar as duas era a ausência
        // de `output` — que o backend deixou de produzir.
        persistida: true,
      }
      if (parte.executor !== EXECUTOR_CLIENTE) return parte
      return this.completarAcaoDeFront(parte)
    },
    /** A parte do turno em andamento com este id, ou `null`. */
    partePorId(toolCallId) {
      if (!toolCallId) return null
      const store = this.$store
      if (!store) return null
      const buscar = store.getters[this.storeModule + '/toolPartPorId']
      if (typeof buscar !== 'function') return null
      return buscar(toolCallId)
    },
    /**
     * O cartão de uma tool de FRONT persistida.
     *
     * O backend grava `{id, name, input, executor: 'client', output}` e nada
     * mais — as quatro tools de front declaram `UI_SCHEMA = None` lá porque o
     * desenho depende do que aconteceu no browser. Quem monta o `ui` é o front,
     * nas duas vidas do cartão, e a montagem é UMA
     * (`ui-registry/chave-de-tool-de-front.js`).
     *
     * O estado sai do `output`: ausente ⇒ o navegador ainda não respondeu, e o
     * cartão volta OFERECIDO, com botão. É o que faz a conversa reaberta
     * mostrar a ação pendente em vez de engoli-la.
     */
    completarAcaoDeFront(parte) {
      const estado = estadoDaAcaoPeloOutput(parte.value)
      // `ui` que já veio no registro MANDA — mesma precedência de `chaveDaParte`:
      // quem o montou (o parser, ou um dia o backend) sabe mais que esta
      // derivação. O que se recalcula é só o estado, que sai do `output`.
      if (parte.ui) return { ...parte, state: estado }
      const label = parte.label || this.labelDaToolDeFront(parte.tool)
      return {
        ...parte,
        label: label,
        state: estado,
        ui: uiDaAcaoDeFront({
          tool: parte.tool,
          label: label,
          input: parte.input,
          motivo: motivoDaAcaoPeloOutput(parte.value),
          namespace: this.storeModule,
          estado: estado,
          // ★ A conversa REABERTA precisa do MESMO botão de fallback que o
          // turno ao vivo mostra: sem eles aqui, um bloqueio persistido
          // reaparece só como texto, sem como o usuário recuperar a ação.
          bloqueado: bloqueadoDaAcaoPeloOutput(parte.value),
          href: hrefDaAcaoPeloOutput(parte.value),
        }),
      }
    },
    /**
     * O rótulo humano da tool, tirado do registry — o backend não persiste
     * `label` no `tool_calls`, e sem ele o cartão reaberto viraria um genérico
     * "Ação sugerida" no lugar de "Abrir cliente".
     */
    labelDaToolDeFront(tool) {
      const chave = chaveDaToolDeFront(tool)
      if (!chave) return ''
      const resolucao = resolverEntrada({ ui: { component: chave } })
      return (resolucao.entrada && resolucao.entrada.label) || ''
    },

    /**
     * As partes que a superfície deve desenhar: as VIVAS do turno mais as que
     * ficaram na última mensagem e ainda não estão vivas ([I-1]).
     *
     * A precedência é a mesma do `parteDaChamada`: a parte viva vence, porque é
     * ela que muda no clique. Sem a segunda metade, a conversa REABERTA perderia
     * o cartão — o slice é do turno, e o turno acabou.
     */
    partesVivasEPersistidas(vivas, ultimaMensagem) {
      const doTurno = vivas || []
      const jaVivas = {}
      doTurno.forEach(function(parte) {
        if (parte && parte.toolCallId) jaVivas[parte.toolCallId] = true
      })
      const chamadas = (ultimaMensagem && ultimaMensagem.tool_calls) || []
      const self = this
      const persistidas = chamadas
        .map(function(chamada, indice) {
          return self.parteDaChamada(chamada || {}, indice)
        })
        .filter(function(parte) {
          return !jaVivas[parte.toolCallId]
        })
      return doTurno.concat(persistidas)
    },
    // `show` = `store.getters['parameters/show']`, que é exatamente o que o
    // mixin global expõe como `$access`. Ausente ⇒ o registry recusa entrada com
    // `feature` (fail-closed): mostrar dado de módulo não contratado dentro da
    // conversa não tem desfazer.
    resolver(parte) {
      return resolverEntrada({
        ui: parte.ui,
        tool: parte.tool,
        show: this.$access || {},
        state: parte.state,
        // ★ O PAYLOAD entra na resolução por um motivo só: preview rico e
        // proposta genérica saem da MESMA tool, com o mesmo nome e o mesmo
        // estado — só o payload os separa (`ehPropostaGenericaDeEscrita`).
        props: propsDaParte(parte),
      })
    },
    // `true` aprovado · `false` reprovado · `null` ainda validando.
    vereditoDoSchema(entrada, parte, chave) {
      if (typeof entrada.schema !== 'function') return true
      // No esqueleto o cartão não lê props — validar ali reprovaria o cartão por
      // props que a tool ainda nem devolveu.
      if (parte.state === ESTADO_ESQUELETO) return true
      const registro = this.validacoes[chaveDaValidacao(chave, parte)]
      if (registro === true || registro === false) return registro
      return null
    },
    /**
     * O cartão de uma tool que FALHOU e não tinha cartão nenhum para desenhar
     * (G-4 da VERIFY it.2).
     *
     * Mesmo componente do fallback — é o cartão de contingência da casa —, com
     * o título trocado: "não consegui montar este cartão" seria falso aqui,
     * porque não havia cartão para montar; o que houve foi a ação falhar.
     */
    /**
     * ★ [T075] A RECUSA da tool, com a frase dela — não com o erro do schema.
     *
     * Mesma moldura do `montarFalhaDaTool` (é o cartão de contingência da
     * casa), com duas diferenças que importam: o título não diz que algo se
     * quebrou, porque nada se quebrou — a tool respondeu, e respondeu "não"; e
     * o texto é a MENSAGEM DELA, que nomeia o cliente e o que faltou.
     */
    montarRecusaDaTool(parte, mensagem, identidade) {
      return {
        identidade: identidade,
        chave: null,
        componente: FallbackCard,
        props: {
          titulo: TITULO_TOOL_RECUSOU,
          tool: parte.label || parte.tool || '',
          erro: mensagem,
        },
        state: parte.state || null,
        toolCallId: parte.toolCallId || null,
        label: parte.label || null,
      }
    },
    montarFalhaDaTool(parte, identidade) {
      return {
        identidade: identidade,
        chave: null,
        componente: FallbackCard,
        props: {
          titulo: TITULO_TOOL_FALHOU,
          tool: parte.label || parte.tool || '',
          erro: textoDaFalha(parte),
        },
        state: ESTADO_ERRO,
        toolCallId: parte.toolCallId || null,
        label: parte.label || null,
      }
    },
    /**
     * A AÇÃO falhou e ninguém mais vai contar isso — ou `null`.
     *
     * Extraído de `resolverParte` para caber no teto de statements do ESLint, e
     * o corte é natural: os dois ramos respondem a MESMA pergunta ("esta falha
     * fica órfã?") por caminhos diferentes.
     *
     * 1. `falhaSemCartao` — a parte não tem cartão nenhum, ou o dono dela é um
     *    computed/a própria tela ([I-11]), e o canal legado que desenharia é
     *    alimentado só pelo `tool_result` de SUCESSO: num turno que falhou, a
     *    tela não põe nada.
     * 2. Sem entrada no registry e em `output-error` — o genérico "não consegui
     *    montar este cartão" contaria a história errada (não foi um cartão que
     *    não montou, foi a AÇÃO que não aconteceu) e ainda engoliria o motivo,
     *    que só o cartão de falha exibe. É onde cai o cartão que NÃO declara
     *    `output-error` nos `states` — a recusa por `estado_nao_suportado`.
     *
     * ★ [G-3, QA rodada-v5 it.3] A guarda de cima é nova, e vem ANTES dos dois:
     * a falha cujo cartão veio do SERVIDOR não é órfã. O backend passou a mandar
     * `ui` junto do erro (`core/sse.py::_mapear_tool_result`), e a write-tool
     * que falha anexa o cartão do estado ERRO dela — `fee-fixo/envio-relatorio`,
     * com destinatário e motivo, é o primeiro. `resolucao.entrada` só existe
     * aqui quando a chave resolveu, a flag está ligada E o cartão DECLARA
     * `output-error`: é o cartão dizendo que sabe desenhar esta falha.
     *
     * Sem ela, o ramo 1 devolvia o genérico justamente nas duas superfícies do
     * Fee Fixo (drawer do Especialista e Guia), que declaram as seis chaves do
     * módulo em `chavesIgnoradas` — e ali a premissa do descarte não vale: a
     * tela só desenha o cartão a partir do `tool_result` de SUCESSO, mas agora o
     * SERVIDOR mandou o cartão da falha junto.
     *
     * ⚠️ A tool de FRONT fica de fora, e de propósito. O `ui` dela é montado
     * AQUI (`_encerrarToolDeFront` → `_uiDaAcao`), não pelo servidor, e
     * `plataforma/acao-oferecida` declara `output-error` desde o [T015] — sem
     * este recorte, a exceção do handler trocaria o `fallback-card` (que diz
     * "A ação não pôde ser concluída: <motivo>") pelo cartão da ação, mudando o
     * desfecho que o [G-4]/[I-11] mediram no QA. Cartão do servidor é contrato
     * novo; o caminho da tool de front continua como estava.
     */
    falhaDaToolOuNada(parte, resolucao, identidade) {
      // ★ [T075] A RECUSA vence QUALQUER falha de resolução, e por isso mora
      // aqui — no ramo que roda ANTES da resolução por chave/estado, não lá
      // embaixo no `montarCartao`. Foi o teste que mostrou o buraco: com a
      // chave desconhecida a mensagem da tool era descartada do mesmo jeito,
      // só trocando "dados fora do contrato" por "cartão desconhecido". A tool
      // respondeu, e a resposta foi "não" COM MOTIVO: o motivo é o conteúdo do
      // turno, não um detalhe de um cartão que nunca ia existir.
      const motivoDaRecusa = recusaDaTool(parte)
      if (motivoDaRecusa) return this.montarRecusaDaTool(parte, motivoDaRecusa, identidade)
      if (this.falhaComCartaoDoServidor(parte, resolucao)) return null
      if (falhaSemCartao(parte, this.chavesIgnoradas)) {
        return this.montarFalhaDaTool(parte, identidade)
      }
      if (!resolucao.entrada && parte.state === ESTADO_ERRO) {
        return this.montarFalhaDaTool(parte, identidade)
      }
      return null
    },
    /**
     * A falha que o PRÓPRIO cartão desenha ([G-3]) — ver `falhaDaToolOuNada`.
     *
     * Três condições, e as três são necessárias: a parte falhou, o cartão dela
     * declara `output-error` (é o que faz `resolucao.entrada` existir em erro) e
     * o `ui` veio do servidor, não do encerramento da tool de front.
     */
    falhaComCartaoDoServidor(parte, resolucao) {
      if (!parte || parte.state !== ESTADO_ERRO) return false
      if (parte.executor === EXECUTOR_CLIENTE) return false
      return Boolean(resolucao && resolucao.entrada)
    },
    /**
     * O que UMA parte desenha: o cartão, o selo, a falha ou o fallback.
     *
     * Extraído do `map` para caber no teto de statements do ESLint — e a ordem
     * dos ramos É a regra: falha primeiro (erro nunca é silencioso), depois a
     * ausência de entrada, depois a proposta sem desfecho (que não tem props
     * para validar) e só então o schema.
     */
    resolverParte(parte) {
      const resolucao = this.resolver(parte)
      const chave = resolucao.chave
      const identidade = parte.toolCallId || chave

      const falha = this.falhaDaToolOuNada(parte, resolucao, identidade)
      if (falha) return falha
      // ★ A tool foi chamada e ainda não respondeu: nada a desenhar — o rótulo
      // único do turno já diz "Consultando ...". ANTES da resolução por
      // estado, de propósito: um cartão que não declara `input-available` nos
      // `states` cairia em `estado_nao_suportado` e viraria um `fallback-card`
      // de erro no meio de todo turno normal (é a régua que o registry do
      // fee-fixo documenta em "os `states` de cada cartão são uma decisão").
      if (esqueletoDoFio(parte)) return null
      // ★ Parte SEM chave de cartão não vira cartão NEM fallback — a regra
      // está escrita no `chaveDaParte`, e o `resolverParte` não a aplicava.
      // `resolverEntrada` devolve `chave_desconhecida` tanto para a chave que
      // o front não conhece quanto para a AUSÊNCIA de chave, e os dois casos
      // são opostos: no primeiro o backend prometeu um cartão e este front não
      // tem (deploy atrasado, e o consultor precisa saber); no segundo ninguém
      // prometeu nada — a tool é de leitura pura e o texto do turno já conta o
      // que ela achou.
      //
      // Medido em 2026-09-04 no app real (drawer do Especialista Fee-Based
      // sobre `/fee-fixo/clientes`): `skill_view` e `get_premissas_fee_fixo`
      // respondiam certo e apareciam como "Não consegui montar este cartão".
      //
      // ⚠️ Só vale fora do erro: a falha SEM cartão já foi tratada nos dois
      // ramos acima, e cair aqui a apagaria — o silêncio sobre o erro é o
      // defeito que o [G-4] fechou.
      if (!chave) return null
      if (!resolucao.entrada) {
        return this.montarFallback(parte, chave, resolucao.motivo, identidade)
      }
      // Os dois desfechos da escrita, e os dois saem do `state` ([G-1] it.5):
      // a proposta que ficou na mensagem vira selo (sem botão), e o commit vira
      // recibo — sem passar o REGISTRO gravado pelo schema do cartão, que ele
      // nunca satisfaz.
      if (propostaPersistidaSemDesfecho(parte, chave)) {
        return this.montarSeloDaProposta(parte, resolucao, identidade)
      }
      if (escritaConcluidaSemProps(parte, chave)) {
        return this.montarReciboDaEscrita(parte, resolucao, identidade)
      }
      return this.montarCartao(parte, resolucao, identidade)
    },
    /** O cartão de verdade — com os props conferidos contra o schema. */
    montarCartao(parte, resolucao, identidade) {
      const chave = resolucao.chave
      const veredito = this.vereditoDoSchema(resolucao.entrada, parte, chave)
      if (veredito === false) {
        return this.montarFallback(parte, chave, MOTIVOS.SCHEMA, identidade)
      }
      // ★ Painel de artefato (Fase 2b): cartão de DADOS cuja apresentação
      // resolve para `painel` ⇒ prévia clicável no lugar do componente real.
      // O getter só diz que HÁ painel nesta superfície; quem decide por
      // cartão é o `ui.apresentacao` do backend (ausente = painel) — ver o
      // cabeçalho e `apresentacao.js`. Tool de front (`executor: 'client'`)
      // fica de fora — ver `ehCartaoDeDados`.
      const cartaoDeDados = ehCartaoDeDados(resolucao.entrada, parte)
      const apresentacao = resolverApresentacao({
        painelDisponivel: this.modoArtefatoAtivo,
        entrada: resolucao.entrada,
        parte: parte,
      })
      if (cartaoDeDados && apresentacao === APRESENTACAO.PAINEL) {
        return this.montarPreviewDeArtefato(
          parte,
          resolucao,
          identidade,
          this.abreNoPainelAutomaticamente(parte, resolucao, veredito)
        )
      }
      const props = propsDaParte(parte)
      const cartao = {
        identidade: identidade,
        chave: chave,
        componente: resolucao.entrada.component,
        props: props,
        // Enquanto o veredito não volta, o cartão desenha o esqueleto.
        state: veredito === true ? parte.state : ESTADO_ESQUELETO,
        // A janela do esqueleto, para a MOLDURA: vira a classe
        // `iv2-parte--validando`, e sair dela é o crossfade esqueleto →
        // conteúdo — na mesma `identidade`, sem remontar o cartão.
        validando: veredito === null,
        toolCallId: parte.toolCallId || null,
        // ★ [task #169] A proposta do Modo Agente, quando o evento a trouxe
        // (`apiChat._onToolApprovalRequest`). `null` é o caso de qualquer outro
        // cartão — e o do Modo Chat, que não tem proposta.
        idProposta: parte.id_proposta || null,
        // ★ [#215] Qual tool produziu a parte. É o elo que liga o cartão à
        // proposta aberta quando a cópia PERSISTIDA não traz `id_proposta`
        // — que é SEMPRE o caso hoje: o backend grava no `tool_calls[]` só
        // `id,name,input,executor,state,output,ui,label,erro`. Sem ele,
        // `utils/proposta-do-cartao.js` não tem por onde casar, e o clique
        // em Confirmar vira a frase de protocolo em vez da decisão.
        tool: parte.tool || null,
        label: parte.label || resolucao.entrada.label || null,
        // A entrada que declara `historicoSomenteLeitura` tem efeito colateral
        // ao montar (polling, download) e a cópia PERSISTIDA não pode
        // reexecutá-lo — desce pela moldura como `somente-leitura`, o mesmo
        // sinal do selo da proposta. Opt-in por entrada: nos cartões de
        // escrita a variante histórica é decisão de produto de cada um.
        somenteLeitura:
          partePersistida(parte) &&
          resolucao.entrada.historicoSomenteLeitura === true,
      }
      // Inline, o cartão de dados continua entrando no índice "Artefatos" —
      // com `parteReal`, para a linha abrir o aside como a prévia abre.
      if (!cartaoDeDados) return cartao
      return this.comMetadadosDeArtefato(cartao, parte, resolucao, props, apresentacao)
    },
    /**
     * O que o índice "Artefatos desta conversa" mostra de um cartão de dados
     * (`artefato`: título/ícone/subtítulo/módulo), o `data-artefato-id` que
     * o invólucro do cartão carrega (`ancoraDeArtefato`) e a resolução REAL
     * que o aside monta (`parteReal`) — o MESMO cálculo nos dois modos,
     * prévia e inline, para a linha do índice não mudar de cara nem de
     * comportamento conforme o cartão. Devolve um objeto NOVO — não muta o
     * cartão recebido.
     */
    comMetadadosDeArtefato(cartao, parte, resolucao, props, apresentacao) {
      const entrada = resolucao.entrada
      const label = parte.label || entrada.label || ''
      return {
        ...cartao,
        artefato: {
          titulo: tituloDoArtefato(label),
          icone: resolverIconeDoModulo(entrada.module),
          subtitulo: this.subtituloDoArtefato(props, entrada.module),
          modulo: entrada.module || '',
        },
        ancoraDeArtefato: cartao.identidade,
        parteReal: this.abreNoAside(entrada)
          ? this.parteRealDe(parte, resolucao, cartao.identidade, props, apresentacao)
          : null,
      }
    },
    /**
     * O cartão pode ser aberto no aside — a partir da prévia OU da linha do
     * índice sobre um cartão inline. Não pode em dois casos: sem painel nesta
     * superfície (`modoArtefatoAtivo` falso: kill-switch, fora da imersiva,
     * Fee Fixo) e cartão de ESCRITA.
     *
     * ★ [spec-005] O cartão de escrita fica de fora por uma razão que MUDOU de
     * forma: o aside hoje LIGA `@respond` e desce a moldura inteira —
     * `tool-call-id`/`turno-em-voo` e, desde 21/09/2026, `state`/
     * `somente-leitura` (`artefato-painel.vue`) —, então a confirmação teria
     * por onde sair E saberia a fase. O que falta é DECISÃO, não plumbing: o
     * `state` que o aside lê é o da `parteReal` do REGISTRO (o do momento em
     * que a bolha registrou a entrega), não a parte VIVA que muda no clique —
     * a proposta decidida na bolha seguiria pendente no aside até o próximo
     * registro. Enquanto isso não for resolvido de propósito, a proposta de
     * escrita continua inline. Nesses dois casos o índice rola até o cartão.
     */
    abreNoAside(entrada) {
      return Boolean(this.modoArtefatoAtivo) && !ehCartaoDeEscrita(entrada)
    },
    /**
     * A resolução REAL — o que `abrirArtefato` guarda e o aside monta.
     *
     * Ver o comentário do cabeçalho do arquivo: é o campo `identidade` daqui
     * que faz o card comparar contra `artefatoIdentidadeAtiva`/
     * `artefatoFoiVisto` depois do clique. `label` é o ORIGINAL (o contrato
     * `label`/`TOOL_LABELS` não muda); só o título do card passa por
     * `tituloDoArtefato`.
     */
    parteRealDe(parte, resolucao, identidade, props, apresentacao) {
      const entrada = resolucao.entrada
      return {
        identidade: identidade,
        componente: entrada.component,
        props: props,
        label: parte.label || entrada.label || '',
        module: entrada.module,
        // ★ A apresentação que o BACKEND pediu, carregada até aqui — quem
        //   precisa dela é a abertura AUTOMÁTICA do fim do turno
        //   (`messages-painel-do-chat-mixin.js`): `inline` quer dizer "mostre
        //   na tela", e abrir o aside sozinho por cima disso contradiz o
        //   próprio pedido (achado do usuário, 18/09/2026, no onboarding).
        //   O CLIQUE na linha do índice continua abrindo o aside para o
        //   inline — isso não muda; o que muda é só o automático.
        apresentacao: apresentacao || null,
        // ★ [spec-005, gap Q-1 do QA de tela] Os dois campos que dão caminho
        // de VOLTA a um cartão de tool interativo aberto no aside. Não são
        // metadado: `chave` é por onde `view-chat.passoLocalDoCartao` acha o
        // confirmador do MÓDULO (sem ela sai o "Confirmo" genérico, que na
        // segmentação reexecuta a tool sem exceção nenhuma), e `toolCallId` é
        // o que identifica a chamada na resposta. Inline os dois já descem
        // pela moldura da bolha; aqui eles precisavam viajar na parte, porque
        // o aside monta a partir dela e de mais nada.
        chave: resolucao.chave,
        toolCallId: parte.toolCallId || null,
        // ★ [owner, 21/09/2026] O `state` e a ORIGEM da parte, para o aside
        // passar a MESMA moldura que a bolha (`artefato-painel.vue`) — a
        // mesma régua do `somenteLeitura` do cartão inline (`montarCartao`).
        // Sem os dois o cartão aberto no aside não sabia a fase nem que veio
        // de uma mensagem — a lacuna que `abreNoAside` registra. Lidos da
        // PARTE porque este objeto é montado também para a PRÉVIA, cujo
        // `cartao` é só `{identidade}`.
        state: parte.state || null,
        somenteLeitura:
          partePersistida(parte) && entrada.historicoSomenteLeitura === true,
      }
    },
    /**
     * O card de prévia do painel de artefato (Fase 2b, plano
     * `moonlit-conjuring-sun`) — substitui o cartão de dados real quando o
     * modo artefato está ligado.
     *
     * Carrega a resolução REAL em `parteReal` (via `comMetadadosDeArtefato`)
     * — é o que `chat-message.vue` despacha para `abrirArtefato` no clique do
     * preview, para o aside montar exatamente o componente/props que seriam
     * desenhados inline.
     *
     * ★ O TÍTULO do card (`props.titulo`) passa por `tituloDoArtefato` — o
     * `label` cru (`entrada.label`) é, por desenho, o mesmo texto de
     * `TOOL_LABELS` do backend ("Consultando cliente..."), certo para a linha
     * de raciocínio mas errado como título permanente de um card já
     * resolvido. `parteReal.label` continua com o `label` ORIGINAL intacto —
     * é o que `chat-message.vue` usa para abrir o aside, e o contrato
     * `label`/`TOOL_LABELS` não muda.
     *
     * @param {boolean} abrirAutomaticamente Vem de `montarCartao` — só é
     *   `true` para a entrada que declara `abrirNoPainelAutomaticamente`
     *   (hoje só `documento/panorama`, spec-013 [T007]/US-5) E já passou pelo
     *   MESMO veredito de schema que qualquer outro cartão exige. É lido pelo
     *   `watch: partesResolvidas` abaixo — nunca pelo clique.
     */
    montarPreviewDeArtefato(parte, resolucao, identidade, abrirAutomaticamente) {
      const entrada = resolucao.entrada
      const label = parte.label || entrada.label || ''
      // ★ [preview do painel de artefato] `props` é lido UMA VEZ e reusado
      // tanto no subtítulo quanto em `parteReal.props` — chamar `propsDaParte`
      // de novo duplicaria trabalho sem motivo (o objeto já foi montado).
      const props = propsDaParte(parte)
      const previa = this.comMetadadosDeArtefato(
        { identidade: identidade },
        parte,
        resolucao,
        props,
        // A prévia SÓ existe quando a apresentação resolveu `painel` (é o
        // `if` que chama esta montagem) — por isso a constante, e não um
        // recálculo que poderia divergir do ramo que nos trouxe aqui.
        APRESENTACAO.PAINEL
      )
      return {
        ...previa,
        chave: resolucao.chave,
        componente: ArtefatoPreviewCard,
        props: {
          // A MESMA identidade usada para o `:key`/dedupe do renderer — é o
          // que o card compara contra `artefatoIdentidadeAtiva`/
          // `artefatoFoiVisto` para saber se é o artefato ATIVO ou já VISTO.
          identidade: identidade,
          // ⚠️ O card compara a identidade contra `artefatoIdentidadeAtiva`
          // do MESMO módulo em que o clique despacha `abrirArtefato` — que é
          // este. Sem a prop ele lia o módulo fixo e a prévia nunca acendia
          // como selecionada na Home IA (conversa viva).
          storeModule: this.storeModule,
          tipo: TIPO_ARTEFATO_PADRAO,
          titulo: previa.artefato.titulo,
          icone: previa.artefato.icone,
          subtitulo: previa.artefato.subtitulo,
        },
        state: parte.state,
        toolCallId: parte.toolCallId || null,
        label: label,
        abrirAutomaticamente: Boolean(abrirAutomaticamente),
        // `parteReal` já veio em `previa` (`comMetadadosDeArtefato`) — o
        // MESMO da versão inline do cartão.
      }
    },
    /**
     * A prévia PODE se abrir sozinha, sem clique (spec-013 [T007]/US-5:
     * "o painel abre com o documento").
     *
     * Três condições, e as três são necessárias — a mesma disciplina de
     * `falhaComCartaoDoServidor`/`abreNoAside` acima:
     *
     * 1. `resolucao.entrada.abrirNoPainelAutomaticamente` — o REGISTRY que
     *    decide, não uma lista de chaves aqui: adicionar um documento novo
     *    não deveria exigir tocar o mixin de novo.
     * 2. `veredito === true` — schema JÁ validado. `null` (ainda validando)
     *    ou `false` (reprovado) nunca abrem: "schema inválido não abre" é
     *    literal do plano de risco da spec (`plan.md` §5).
     * 3. `!partePersistida(parte)` — só a parte VIVA do turno atual. Uma
     *    conversa REABERTA reconstrói o histórico via `parteDaChamada`
     *    (`persistida: true`) com este MESMO resultado, e sem esta guarda o
     *    documento de uma conversa antiga abriria sozinho ao trocar/recarregar
     *    a conversa — o risco nomeado no `plan.md` ("Documento reabrindo
     *    sozinho: replay, histórico ou remontagem disparam o watcher").
     */
    abreNoPainelAutomaticamente(parte, resolucao, veredito) {
      const entrada = resolucao.entrada
      if (!entrada || entrada.abrirNoPainelAutomaticamente !== true) return false
      if (veredito !== true) return false
      return !partePersistida(parte)
    },
    /**
     * O subtítulo do card de prévia: `props.nome` (o campo que
     * `cliente/resumo` já devolve) OU uma contagem fácil de extrair, seguido
     * SEMPRE do nome do módulo quando ele resolve (`resolverNomeDoModulo`) —
     * "Inez · CRM", "1.667 linhas · CRM", ou só "CRM" quando nenhum dos dois
     * primeiros existe.
     */
    subtituloDoArtefato(props, moduleKey) {
      const partes = [
        this.parteDoConteudoDoArtefato(props),
        resolverNomeDoModulo(moduleKey),
      ].filter(Boolean)
      return partes.join(' · ')
    },
    /**
     * `props.nome` (string não vazia) tem prioridade — é o campo que
     * `cliente/resumo` já devolve. Sem ele, tenta uma contagem fácil (nesta
     * ordem: `props.total` numérico, `props.itens.length`, `props.items.length`)
     * e formata como "{N} linhas", com separador de milhar pt-BR
     * (`formatNumber`, `@utils/Format`). Sem nenhum dos dois, `null` — o
     * cartão sem essa informação (a maioria, ex. `alertas`/`tarefas`) não
     * inventa um número.
     */
    parteDoConteudoDoArtefato(props) {
      if (props && typeof props.nome === 'string' && props.nome) return props.nome
      const contagem = this.contagemDoArtefato(props)
      if (contagem === null) return null
      return formatNumber(contagem, 0) + ' linhas'
    },
    contagemDoArtefato(props) {
      if (!props) return null
      if (typeof props.total === 'number') return props.total
      if (Array.isArray(props.itens)) return props.itens.length
      if (Array.isArray(props.items)) return props.items.length
      return null
    },
    /**
     * A proposta reaberta sem desfecho ([G-1] + [G-3] do design).
     *
     * O que a torna inerte é `somenteLeitura`, não a falta de payload: o
     * registro TEM o resumo e os rótulos, e escondê-los fazia o thread reaberto
     * mostrar uma linha de texto solta, sem dizer o que chegou a ser proposto.
     *
     * ★ Os props ainda passam pelo schema — só que reprovar aqui não vira
     * `fallback-card`: cai no selo sem conteúdo. Cartão de erro sobre uma
     * escrita já resolvida é exatamente o que o [G-1] fechou.
     */
    montarSeloDaProposta(parte, resolucao, identidade) {
      const veredito = this.vereditoDoSchema(resolucao.entrada, parte, resolucao.chave)
      const cartao = this.cartaoPeloEstado(parte, resolucao, identidade)
      cartao.props = veredito === true ? propsDaParte(parte) : {}
      cartao.somenteLeitura = true
      return cartao
    },
    /**
     * O recibo do COMMIT ([G-1] it.5).
     *
     * Mesmo caminho do selo, e pelo mesmo motivo: o que o cartão precisa saber
     * está no `state`, e o `output` do commit é o registro gravado — dado, não
     * props. É por isso que ele NÃO passa pelo schema: `ui-crm-confirmacao`
     * descreve o preview, e a linha de `pm_task` reprovaria sempre.
     */
    montarReciboDaEscrita(parte, resolucao, identidade) {
      return this.cartaoPeloEstado(parte, resolucao, identidade)
    },
    /** O cartão do registry desenhado só pelo ESTADO, sem props nem schema. */
    cartaoPeloEstado(parte, resolucao, identidade) {
      return {
        identidade: identidade,
        chave: resolucao.chave,
        componente: resolucao.entrada.component,
        props: {},
        state: parte.state,
        toolCallId: parte.toolCallId || null,
        label: parte.label || resolucao.entrada.label || null,
      }
    },
    /**
     * ★ [T075] O schema reprovou — mas ANTES de chamar isso de violação de
     * contrato, veja se o payload não é um cartão por um motivo LEGÍTIMO.
     *
     * Hoje há um: o dado foi para FORA do evento (offload acima de 8 KiB) e o
     * que chegou é um ponteiro. Dizer "dados fora do contrato" ali aponta para
     * o lugar errado — quem lê vai caçar schema, e o que houve foi tamanho.
     *
     * A RECUSA da tool não passa por aqui: ela é resolvida antes, no
     * `falhaDaToolOuNada`, porque tem de vencer até a chave desconhecida.
     */
    montarFallback(parte, chave, motivo, identidade) {
      return {
        identidade: identidade,
        chave: chave,
        componente: FallbackCard,
        props: { chave: chave || '', motivo: motivo, tool: parte.tool || '' },
        state: null,
        toolCallId: null,
        label: null,
      }
    },
    /**
     * Abre sozinho cada cartão resolvido que pediu abertura automática
     * (spec-013 [T007]/US-5) e ainda não abriu NESTA montagem do renderer.
     *
     * Varre `partesResolvidas` — não `partesParaRenderizar` — porque só o
     * cartão JÁ resolvido carrega `abrirAutomaticamente` (a flag nasce em
     * `montarPreviewDeArtefato`, depois de `resolverParte` decidir falha,
     * schema e apresentação). "Abre uma vez por turno/`toolCallId`" é este
     * mapa: a MESMA identidade nunca dispara duas vezes nesta instância,
     * então um replay do stream (o mesmo `tool-output-available` chegando de
     * novo após uma reconexão) ou um novo token do texto (que recalcula
     * `partesResolvidas` inteiro) não reabrem o painel.
     */
    autoAbrirDocumentos(partes) {
      const lista = partes || []
      lista.forEach((cartao) => {
        this.abrirArtefatoAutomaticamenteSeNecessario(cartao)
      })
    },
    /**
     * O getter existe só nas superfícies que compõem o slice de artefato (a
     * imersiva). Sem ele — Fee Fixo, `ai-framework`, a página cheia — a
     * resposta é `false` e o comportamento fica byte a byte o de antes: lá a
     * abertura automática é governada só pelas duas guardas originais.
     */
    artefatoJaAbriuSozinho(identidade) {
      const store = this.$store
      if (!store || !store.getters) return false
      const consultar = store.getters[this.storeModule + '/artefatoJaAbriuAutomaticamente']
      if (typeof consultar !== 'function') return false
      return consultar(identidade) === true
    },
    abrirArtefatoAutomaticamenteSeNecessario(cartao) {
      if (!cartao || cartao.abrirAutomaticamente !== true) return
      if (!cartao.parteReal || !cartao.identidade) return
      if (this.documentosAbertosAutomaticamente[cartao.identidade]) return
      // ★ **[18/09/2026] A mesma pergunta, agora no STORE** — e é ela que
      //   fecha o caso que o mapa acima não alcança: voltar ao chat SEM F5
      //   remonta este renderer (mapa local zerado) com a parte do turno ainda
      //   VIVA no slice `toolParts`, ou seja, não persistida — as duas guardas
      //   existentes passam batido e o `/panorama` reabre sozinho, mesmo tendo
      //   sido FECHADO à mão ("sendo que já fechei antes", owner).
      //
      //   Abrir sozinho é gesto do TURNO: acontece UMA vez por entrega, e a
      //   marca morre com a conversa (`LIMPAR_ARTEFATOS_DA_CONVERSA`).
      if (this.artefatoJaAbriuSozinho(cartao.identidade)) return
      // Coerência de exibição (§5.1): enquanto as etapas do plano ainda estão
      // aparecendo no cartão da conversa, o aside não rouba a tela. Vale só na
      // Home IA — é lá que alguém alimenta o relógio da encenação; nas outras
      // superfícies `encenacaoDrenando()` é sempre `false`. O TEXTO não espera.
      if (this.esperarEncenacaoDoPlano(cartao)) return

      this.documentosAbertosAutomaticamente = {
        ...this.documentosAbertosAutomaticamente,
        [cartao.identidade]: true,
      }
      const store = this.$store
      if (!store || typeof store.dispatch !== 'function') return
      // Marca ANTES de abrir: o dispatch de abertura muda o store e pode
      // reentrar neste caminho pelo watcher de `partesResolvidas`.
      store.dispatch(
        this.storeModule + '/marcarArtefatoAbertoAutomaticamente',
        cartao.identidade
      )
      store.dispatch(this.storeModule + '/abrirArtefato', cartao.parteReal)
    },
    /**
     * `true` quando a abertura foi ADIADA: reagenda-se sozinha quando a fila
     * da encenação termina (o `unwatch` evita observador pendurado).
     */
    esperarEncenacaoDoPlano(cartao) {
      if (!encenacaoDrenando()) return false
      if (typeof this.$watch !== 'function') return false
      const pararDeObservar = this.$watch(
        () => encenacaoDrenando(),
        (drenando) => {
          if (drenando) return
          pararDeObservar()
          this.abrirArtefatoAutomaticamenteSeNecessario(cartao)
        }
      )
      return true
    },
    agendarValidacoes() {
      const self = this
      // ★ [T075] A MESMA lista que o desenho usa — ver `partesNormalizadas`.
      ;(this.partesNormalizadas || []).forEach(function(parte) {
        // Parte que não vai desenhar não paga chunk de schema ([G-1]).
        if (semPayloadParaDesenhar(parte)) return
        if (parte.state === ESTADO_ESQUELETO) return
        const resolucao = self.resolver(parte)
        const entrada = resolucao.entrada
        if (!entrada || typeof entrada.schema !== 'function') return
        // O commit não tem props para conferir: o `output` dele é o registro
        // gravado, e o schema do cartão descreve o preview. A PROPOSTA
        // persistida continua validando — ela desenha o resumo ([G-3]).
        if (escritaConcluidaSemProps(parte, resolucao.chave)) return

        const chave = chaveDaValidacao(resolucao.chave, parte)
        if (Object.prototype.hasOwnProperty.call(self.validacoes, chave)) return

        self.validacoes = { ...self.validacoes, [chave]: null }
        validarProps(entrada, propsDaParte(parte)).then(function(r) {
          self.validacoes = { ...self.validacoes, [chave]: Boolean(r && r.ok) }
        })
      })
    },
  },
}

export default renderizaPartesDoTurno
